import { Injectable } from '@angular/core';
import { NumberUtils } from '../../../../aumico-lib-core-fe/src/lib';
import { GetOrganizationDto } from '@modeso/aumico-lib-users-fe';
import { IAnnualReport } from '@modeso/types__aumico-lib-annualreport-be';
import { IDynamicKeyValue } from '@modeso/types__aumico-lib-core-be';
import { DefaultGroupsNames } from '@modeso/types__aumico-lib-templates-be';
import { AumFinancialNumberPipe, DateLocalePipe } from '../../../../aumico-lib-core-fe/src/lib';

@Injectable({ providedIn: 'root' })
export class PlaceholderHelper {
    public static getPlaceholderMap(
        annualReport: IAnnualReport,
        dateLocalePipe: DateLocalePipe,
        finNumberPipe: AumFinancialNumberPipe,
    ): IDynamicKeyValue {
        const placeHolders: IDynamicKeyValue = {};
        if (annualReport.generalInformation == null) {
            return placeHolders;
        }

        const todaysDate = new Date();
        placeHolders['@today'] = dateLocalePipe.transform(todaysDate).toString();

        placeHolders['@ar.company'] =
            annualReport.generalInformation.company.name != null ? annualReport.generalInformation.company.name : '';
        placeHolders['@company.name'] =
            annualReport.generalInformation.company.name != null ? annualReport.generalInformation.company.name : '';
        placeHolders['@company.street'] =
            annualReport.generalInformation.company.street != null
                ? annualReport.generalInformation.company.street
                : '';
        placeHolders['@company.addition-to-address'] =
            annualReport.generalInformation.company.additionToAddress != null
                ? annualReport.generalInformation.company.additionToAddress
                : '';
        placeHolders['@company.zip-code'] =
            annualReport.generalInformation.company.zip != null ? annualReport.generalInformation.company.zip : '';
        placeHolders['@company.city'] =
            annualReport.generalInformation.company.city != null ? annualReport.generalInformation.company.city : '';
        placeHolders['@company.country'] =
            annualReport.generalInformation.company.country.name != null
                ? annualReport.generalInformation.company.country.name
                : '';
        placeHolders['@company.vat-id'] =
            annualReport.generalInformation.company.UID != null ? annualReport.generalInformation.company.UID : '';

        placeHolders['@ar.duedate'] = dateLocalePipe
            .transform(annualReport.generalInformation.balanceSheetDate)
            .toString();
        placeHolders['@ar.duedate-of-previous-year'] = dateLocalePipe
            .transform(annualReport.generalInformation.balancePreviousYearDate)
            .toString();

        placeHolders['@ar.duedate-of-previous-year'] = dateLocalePipe
            .transform(annualReport.generalInformation.balancePreviousYearDate)
            .toString();

        placeHolders['@ar.year'] = new Date(annualReport.generalInformation.balanceSheetDate).getFullYear().toString();
        placeHolders['@ar.previous-year'] = new Date(annualReport.generalInformation.balancePreviousYearDate)
            .getFullYear()
            .toString();

        placeHolders['@ar.period'] = `${dateLocalePipe
            .transform(annualReport.generalInformation.incomeCurrentYearFromDate)
            .toString()}-${dateLocalePipe
            .transform(annualReport.generalInformation.incomeCurrentYearToDate)
            .toString()}`;
        placeHolders['@ar.period-of-previous-year'] = `${dateLocalePipe
            .transform(annualReport.generalInformation.incomePreviousYearFromDate)
            .toString()}-${dateLocalePipe
            .transform(annualReport.generalInformation.incomePreviousYearToDate)
            .toString()}`;

        placeHolders['@ar.assets'] = finNumberPipe.transform(
            annualReport.finStatements[0]?.templateAndAccountsMapping?.template?.group?.groups.find(
                (group) => group.name === DefaultGroupsNames.ASSETS,
            ).calculatedClosingBalance,
        );
        placeHolders['@ar.assets-of-previous-year'] = finNumberPipe.transform(
            annualReport.finStatements[0]?.templateAndAccountsMapping?.template?.group?.groups.find(
                (group) => group.name === DefaultGroupsNames.ASSETS,
            ).calculatedOpeningBalance,
        );
        placeHolders['@ar.liabilities'] = finNumberPipe.transform(
            annualReport.finStatements[0]?.templateAndAccountsMapping?.template?.group?.groups.find(
                (group) => group.name === DefaultGroupsNames.LIABILITIES,
            ).calculatedClosingBalance,
        );
        placeHolders['@ar.liabilities-of-previous-year'] = finNumberPipe.transform(
            annualReport.finStatements[0]?.templateAndAccountsMapping?.template?.group?.groups.find(
                (group) => group.name === DefaultGroupsNames.LIABILITIES,
            ).calculatedOpeningBalance,
        );
        placeHolders['@ar.profit'] = finNumberPipe.transform(
            annualReport.finStatements[1]?.templateAndAccountsMapping?.template?.group?.calculatedClosingBalance,
        );
        placeHolders['@ar.profit-of-previous-year'] = finNumberPipe.transform(
            annualReport.finStatements[1]?.templateAndAccountsMapping?.template?.group?.calculatedOpeningBalance,
        );

        // FX (REV-21512): on non-FX reports (or while a rate is missing) these resolve to '' —
        // the placeholders are never gated, an empty value is the whole contract (N1.5/N1.6).
        // Rates print at 5 decimals, exactly like the conversion table's rate cells.
        const formatFxRate = NumberUtils.formatFxRate;
        const fxTranslation = annualReport.fxTranslation;
        placeHolders['@ar.display-currency'] = annualReport.generalInformation.displayCurrency?.name ?? '';
        placeHolders['@ar.closing-rate'] = formatFxRate(fxTranslation?.currentYear?.closing);
        placeHolders['@ar.closing-rate-of-previous-year'] = formatFxRate(fxTranslation?.previousYear?.closing);
        placeHolders['@ar.average-rate'] = formatFxRate(fxTranslation?.currentYear?.average);
        placeHolders['@ar.average-rate-of-previous-year'] = formatFxRate(fxTranslation?.previousYear?.average);

        return placeHolders;
    }

    public static setPlaceholderForUser(map: IDynamicKeyValue, userName: string): IDynamicKeyValue {
        if (!map) {
            map = {} as IDynamicKeyValue;
        }
        map['@user.name'] = userName;
        return map;
    }

    public static setPlaceholderFoOrganization(
        map: IDynamicKeyValue,
        organization: GetOrganizationDto,
    ): IDynamicKeyValue {
        if (!map) {
            map = {} as IDynamicKeyValue;
        }
        map['@org.name'] = organization.name != null ? organization.name : '';
        map['@org.street'] = organization.street != null ? organization.street : '';
        map['@org.addition-to-address'] = organization.additionToAddress != null ? organization.additionToAddress : '';
        map['@org.zip-code'] = organization.zip != null ? organization.zip : '';
        map['@org.city'] = organization.city != null ? organization.city : '';

        return map;
    }

    public static setAopSnapshotPlaceholder(map: IDynamicKeyValue, aopSnapshot: string | undefined): IDynamicKeyValue {
        if (!map) {
            map = {} as IDynamicKeyValue;
        }
        if (aopSnapshot != null) {
            map['@aop.snapshot'] = `<div [innerHTML]=${aopSnapshot}</div>`;
        } else {
            map['@aop.snapshot'] = '';
        }
        return map;
    }

    public static setAopSnapshotCurrentYearPlaceholder(
        map: IDynamicKeyValue,
        aopSnapshotCurrentYear: string | undefined,
    ): IDynamicKeyValue {
        if (!map) {
            map = {} as IDynamicKeyValue;
        }
        if (aopSnapshotCurrentYear != null) {
            map['@aop.snapshot-current-year'] = `<div [innerHTML]=${aopSnapshotCurrentYear}</div>`;
        } else {
            map['@aop.snapshot-current-year'] = '';
        }
        return map;
    }

    public static resetPlaceHolderOfAnnualReport(map: IDynamicKeyValue): IDynamicKeyValue {
        const organizationAndUsersData = {};
        organizationAndUsersData['@user.name'] = map['@user.name'];
        organizationAndUsersData['@org.name'] = map['@org.name'];
        organizationAndUsersData['@org.street'] = map['@org.street'];
        organizationAndUsersData['@org.addition-to-address'] = map['@org.addition-to-address'];
        organizationAndUsersData['@org.zip-code'] = map['@org.zip-code'];
        organizationAndUsersData['@org.city'] = map['@org.city'];

        return organizationAndUsersData;
    }
}
